import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Flame, Zap, Star, Trophy, Target,
  Clock, TrendingUp, Coffee, MessageCircle, Award,
  ChevronRight, Play, Volume2,
} from 'lucide-react';

// ─── Mock Data ──────────────────────────────────────────────────────

interface ShiftEvent {
  time: string; // HH:MM
  offsetMinutes: number;
  type: 'greeting' | 'upsell_success' | 'upsell_attempt' | 'drink_promo' | 'streak' | 'achievement';
  title: string;
  detail: string;
  points: number;
  mood: 'great' | 'good' | 'neutral' | 'miss';
}

const SHIFT_EVENTS: ShiftEvent[] = [
  { time: '11:02', offsetMinutes: 2, type: 'greeting', title: 'Warm Welcome', detail: '"Welcome to Gen Korean BBQ! First time?" — Natural and friendly', points: 3, mood: 'great' },
  { time: '11:08', offsetMinutes: 8, type: 'upsell_success', title: 'Steak Shawarma Upsell', detail: '"Our Wagyu platter is incredible today" — Guest added it!', points: 10, mood: 'great' },
  { time: '11:15', offsetMinutes: 15, type: 'drink_promo', title: 'Drink Promotion', detail: 'Suggested the Mango Lassi special. Guest ordered 2!', points: 5, mood: 'great' },
  { time: '11:23', offsetMinutes: 23, type: 'greeting', title: 'Quick Hello', detail: 'Greeted table 7 — slightly rushed but still warm', points: 3, mood: 'good' },
  { time: '11:31', offsetMinutes: 31, type: 'upsell_attempt', title: 'Döner Kebab Pitch', detail: 'Suggested the döner but guest already knew what they wanted', points: 2, mood: 'neutral' },
  { time: '11:38', offsetMinutes: 38, type: 'streak', title: '3-Streak!', detail: 'Three successful interactions in a row. Keep the momentum!', points: 15, mood: 'great' },
  { time: '11:45', offsetMinutes: 45, type: 'upsell_success', title: 'Dubai Chocolate Upsell', detail: '"Have you tried our Dubai Chocolate dessert?" — Nailed it!', points: 10, mood: 'great' },
  { time: '11:52', offsetMinutes: 52, type: 'drink_promo', title: 'Drink Promotion', detail: 'Recommended the house iced tea. Guest went for it.', points: 5, mood: 'good' },
  { time: '12:01', offsetMinutes: 61, type: 'greeting', title: 'Fantastic Greeting', detail: '"Hey! Welcome back!" — Recognized a regular. Gold star moment.', points: 3, mood: 'great' },
  { time: '12:08', offsetMinutes: 68, type: 'upsell_success', title: 'Steak Shawarma Upsell', detail: 'Table of 4, suggested for the whole group. 3 ordered it!', points: 10, mood: 'great' },
  { time: '12:15', offsetMinutes: 75, type: 'achievement', title: 'Achievement Unlocked!', detail: '"Triple Threat" — 3 upsells in under 90 minutes', points: 50, mood: 'great' },
  { time: '12:22', offsetMinutes: 82, type: 'upsell_attempt', title: 'Dessert Suggestion', detail: 'Offered Dubai Chocolate but table was in a rush', points: 2, mood: 'neutral' },
  { time: '12:30', offsetMinutes: 90, type: 'streak', title: '5-Streak!', detail: 'Five in a row! You\'re on fire. Bonus multiplier active.', points: 25, mood: 'great' },
  { time: '12:38', offsetMinutes: 98, type: 'drink_promo', title: 'Drink Promotion', detail: 'Promoted the new Coconut Cold Brew. Two guests tried it!', points: 5, mood: 'great' },
  { time: '12:45', offsetMinutes: 105, type: 'upsell_success', title: 'Döner Kebab Upsell', detail: '"The döner is our most popular — you won\'t regret it"', points: 10, mood: 'great' },
  { time: '12:55', offsetMinutes: 115, type: 'streak', title: '7-Streak! New Record!', detail: 'SEVEN IN A ROW! This is your new personal best!', points: 50, mood: 'great' },
];

const ACHIEVEMENTS = [
  { name: 'Triple Threat', description: '3 upsells in 90 minutes', icon: Zap, unlocked: true },
  { name: 'On Fire', description: '5+ interaction streak', icon: Flame, unlocked: true },
  { name: 'Record Breaker', description: 'New personal streak record', icon: Trophy, unlocked: true },
  { name: 'Drink Master', description: '5 drink promotions in one shift', icon: Coffee, unlocked: false },
  { name: 'People Person', description: '100% greeting rate', icon: MessageCircle, unlocked: false },
  { name: 'The Closer', description: '80%+ upsell success rate', icon: Target, unlocked: false },
];

// ─── Component ──────────────────────────────────────────────────────

const ShiftReplay: React.FC = () => {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (isPlaying && visibleCount < SHIFT_EVENTS.length) {
      const timer = setTimeout(() => {
        setVisibleCount(c => c + 1);
      }, 400);
      return () => clearTimeout(timer);
    } else if (visibleCount >= SHIFT_EVENTS.length) {
      setIsPlaying(false);
    }
  }, [isPlaying, visibleCount]);

  const totalPoints = SHIFT_EVENTS.slice(0, visibleCount).reduce((sum, e) => sum + e.points, 0);
  const successes = SHIFT_EVENTS.slice(0, visibleCount).filter(e => e.type === 'upsell_success').length;
  const attempts = SHIFT_EVENTS.slice(0, visibleCount).filter(e => e.type === 'upsell_success' || e.type === 'upsell_attempt').length;

  const moodColor = {
    great: 'border-emerald-400 bg-emerald-50',
    good: 'border-blue-300 bg-blue-50',
    neutral: 'border-gray-300 bg-gray-50',
    miss: 'border-red-300 bg-red-50',
  };

  const moodDot = {
    great: 'bg-emerald-400',
    good: 'bg-blue-400',
    neutral: 'bg-gray-400',
    miss: 'bg-red-400',
  };

  const typeIcon: Record<string, React.ComponentType<{ className?: string }>> = {
    greeting: MessageCircle,
    upsell_success: Zap,
    upsell_attempt: Target,
    drink_promo: Coffee,
    streak: Flame,
    achievement: Trophy,
  };

  const typeColor: Record<string, string> = {
    greeting: 'text-blue-500',
    upsell_success: 'text-emerald-500',
    upsell_attempt: 'text-gray-500',
    drink_promo: 'text-purple-500',
    streak: 'text-orange-500',
    achievement: 'text-amber-500',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-orange-100 hover:text-white text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Shift Replay</h1>
              <p className="text-orange-100 text-sm">Today, March 14 · 11:00 AM – 1:00 PM · Gen Korean BBQ, Fremont</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (visibleCount >= SHIFT_EVENTS.length) {
                    setVisibleCount(0);
                  }
                  setIsPlaying(!isPlaying);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg text-white text-sm font-medium hover:bg-white/30 transition-colors"
              >
                <Play className="w-4 h-4" />
                {isPlaying ? 'Playing...' : visibleCount >= SHIFT_EVENTS.length ? 'Replay' : 'Play'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Live stats bar */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Points Earned', value: totalPoints, icon: Star, color: 'text-amber-500' },
            { label: 'Upsell Rate', value: attempts > 0 ? `${Math.round((successes / attempts) * 100)}%` : '—', icon: Target, color: 'text-emerald-500' },
            { label: 'Events', value: visibleCount, icon: Zap, color: 'text-blue-500' },
            { label: 'Best Streak', value: visibleCount >= 16 ? 7 : visibleCount >= 13 ? 5 : visibleCount >= 6 ? 3 : 0, icon: Flame, color: 'text-orange-500' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-3 text-center">
              <s.icon className={`w-4 h-4 ${s.color} mx-auto mb-1`} />
              <div className="text-lg font-bold text-gray-900 transition-all">{s.value}</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Shift Timeline</h2>

          {/* Progress bar */}
          <div className="relative h-2 bg-gray-100 rounded-full mb-6 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-400 to-red-400 rounded-full transition-all duration-300"
              style={{ width: `${(visibleCount / SHIFT_EVENTS.length) * 100}%` }}
            />
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[22px] top-0 bottom-0 w-0.5 bg-gray-100" />

            <div className="space-y-0">
              {SHIFT_EVENTS.map((event, idx) => {
                const Icon = typeIcon[event.type] || Star;
                const isVisible = idx < visibleCount;
                const isNew = idx === visibleCount - 1 && isPlaying;
                return (
                  <div
                    key={idx}
                    className={`relative flex items-start gap-4 py-3 transition-all duration-500 ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 h-0 overflow-hidden py-0'
                    } ${isNew ? 'scale-[1.02]' : ''}`}
                  >
                    {/* Timeline dot */}
                    <div className="relative z-10 flex-shrink-0">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition-all ${
                        event.type === 'achievement' || event.type === 'streak'
                          ? 'border-amber-400 bg-amber-50 shadow-lg shadow-amber-200/50'
                          : moodColor[event.mood]
                      }`}>
                        <Icon className={`w-5 h-5 ${typeColor[event.type]}`} />
                      </div>
                    </div>

                    {/* Content */}
                    <div className={`flex-1 min-w-0 pb-3 border-b border-gray-50 ${
                      event.type === 'achievement' ? 'bg-amber-50/50 -mx-2 px-2 rounded-lg' : ''
                    }`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{event.title}</span>
                            {event.type === 'achievement' && (
                              <span className="text-[10px] font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full animate-pulse">NEW!</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{event.detail}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-xs font-bold ${
                            event.points >= 25 ? 'text-amber-500' : event.points >= 10 ? 'text-emerald-500' : 'text-gray-400'
                          }`}>
                            +{event.points} pts
                          </span>
                          <span className="text-xs text-gray-400">{event.time}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {visibleCount === 0 && (
              <div className="text-center py-12 text-gray-400">
                <Play className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Press Play to replay your shift</p>
              </div>
            )}
          </div>
        </div>

        {/* Achievements */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Achievements</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {ACHIEVEMENTS.map((a, i) => (
              <div
                key={i}
                className={`rounded-lg border p-4 text-center transition-all ${
                  a.unlocked
                    ? 'border-amber-200 bg-amber-50'
                    : 'border-gray-200 bg-gray-50 opacity-50'
                }`}
              >
                <div className={`w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center ${
                  a.unlocked ? 'bg-amber-100' : 'bg-gray-200'
                }`}>
                  <a.icon className={`w-5 h-5 ${a.unlocked ? 'text-amber-500' : 'text-gray-400'}`} />
                </div>
                <div className={`text-xs font-semibold ${a.unlocked ? 'text-amber-800' : 'text-gray-400'}`}>{a.name}</div>
                <div className="text-[10px] text-gray-500 mt-0.5">{a.description}</div>
                {a.unlocked && (
                  <div className="mt-1.5 inline-flex items-center gap-0.5 text-[10px] text-emerald-600 font-medium">
                    <Award className="w-3 h-3" /> Unlocked!
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* End summary */}
        {visibleCount >= SHIFT_EVENTS.length && (
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-200 p-6 text-center">
            <Trophy className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-gray-900 mb-1">Amazing Shift!</h3>
            <p className="text-sm text-gray-600 mb-3">
              You earned <span className="font-bold text-amber-600">{totalPoints} points</span> and hit a
              <span className="font-bold text-orange-600"> 7-streak personal record</span>.
            </p>
            <p className="text-xs text-gray-500">
              Your estimated earnings boost from this shift: <span className="font-bold text-emerald-600">+$42</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShiftReplay;
